/**
 * Dutch display formatting, shared so the API and the web app render numbers the same.
 * UI language is Dutch; identifiers stay English-ish where they are generic.
 */
const LOCALE = 'nl-NL';

/** "4250" -> "EUR 4.250" (with a real euro sign and a non-breaking space). */
export function formatEuro(bedrag: number | null | undefined): string {
  if (bedrag == null || !Number.isFinite(bedrag)) return '-';
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(bedrag);
}

/** "6150" -> "6,2k" - compact money for map labels and tight table cells. */
export function formatEuroCompact(bedrag: number | null | undefined): string {
  if (bedrag == null || !Number.isFinite(bedrag)) return '-';
  if (Math.abs(bedrag) < 1000) return formatEuro(bedrag);
  if (Math.abs(bedrag) < 1_000_000) {
    return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 }).format(bedrag / 1000)}k`;
  }
  return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 }).format(
    bedrag / 1_000_000,
  )} mln`;
}

/** "8123" -> "EUR 8.123/m2". */
export function formatPrijsPerM2(prijsPerM2: number | null | undefined): string {
  if (prijsPerM2 == null || !Number.isFinite(prijsPerM2)) return '-';
  return `${formatEuro(prijsPerM2)}/m\u00b2`;
}

/** "84" -> "84 m2". */
export function formatOppervlakte(m2: number | null | undefined): string {
  if (m2 == null || !Number.isFinite(m2)) return '-';
  return `${new Intl.NumberFormat(LOCALE).format(m2)}\u00a0m\u00b2`;
}

/** Plain number with Dutch thousand separators. */
export function formatAantal(aantal: number | null | undefined): string {
  if (aantal == null || !Number.isFinite(aantal)) return '-';
  return new Intl.NumberFormat(LOCALE).format(aantal);
}

/** ISO timestamp -> "16 sep 2026". */
export function formatDatum(iso: string | Date | null | undefined): string {
  if (iso == null) return '-';
  const datum = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(datum.getTime())) return '-';
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(datum);
}

/** ISO timestamp -> "16 sep 2026 14:05". */
export function formatDatumTijd(iso: string | Date | null | undefined): string {
  if (iso == null) return '-';
  const datum = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(datum.getTime())) return '-';
  return new Intl.DateTimeFormat(LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(datum);
}
